import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

const VideoContainer = styled.div`
  height: 50vh;
  aspect-ratio: 9/16;
  position: relative;
  border-radius: 14px;
  max-height: 100vh;

  
  @media (max-width: 768px) {
    height: auto;
    width: 95%;
    aspect-ratio: 9/16;
    max-width: 100%;
    max-height: 60vh;
    border-radius: 14px;
  }
`;





const YoutubeIframe = React.memo(({ videoId, onVideoEnd, onVideoReady, autoplay = 0, mute = 0, loop = 1, shortId }) => {
  const youtubePlayer = useRef(null);
  const iframeId = `youtube-player-${videoId}-${shortId || uuidv4()}`; // 고유한 ID
  const autoplayAttempted = useRef(false); // 자동재생 시도 여부 추적
  
  // ref 기반 카운트다운 관리
  const countdownRef = useRef({
    currentCount: 5,
    timerId: null,
    isPaused: false
  });
  
  // 독립적인 완료 상태 관리 (useState + useRef)
  const [isVideoReady, setIsVideoReady] = useState(false);
  const isVideoReadyRef = useRef(false);
  
  // 최신 콜백 함수들을 ref로 저장
  const onVideoEndRef = useRef(onVideoEnd);
  const onVideoReadyRef = useRef(onVideoReady);
  
  // 콜백 함수 업데이트
  useEffect(() => {
    onVideoEndRef.current = onVideoEnd;
    onVideoReadyRef.current = onVideoReady;
  }, [onVideoEnd, onVideoReady]);
  
  // 자동재생 시도 함수
  const attemptAutoplay = useCallback(() => {
    if (!youtubePlayer.current || autoplayAttempted.current || autoplay !== 1) {
      return;
    }
    
    autoplayAttempted.current = true;
    
    try {
      // mute 상태 먼저 설정
      if (mute === 1) {
        youtubePlayer.current.mute();
      }
      // 재생 시도
      youtubePlayer.current.playVideo();
      console.log('자동재생 시도됨');
    } catch (error) {
      console.warn('자동재생 실패:', error);
      autoplayAttempted.current = false; // 실패 시 다시 시도 가능하도록
    }
  }, [autoplay, mute]);
  


  // 전역 YouTube API 초기화 큐 설정
  window._youtubeInitQueue = window._youtubeInitQueue || [];
  window._ytApiReadyWrapped = window._ytApiReadyWrapped || false;

  if (!window._ytApiReadyWrapped) {
    window._ytApiReadyWrapped = true;
    window.onYouTubeIframeAPIReady = () => {
      window._youtubeInitQueue.forEach(fn => fn());
      window._youtubeInitQueue = [];
    };
  }

  // YouTube iframe API 초기화
  useEffect(() => {
    if (!videoId) return;
    
    // 새로운 영상이 들어오면 모든 상태 완전 초기화
    if (youtubePlayer.current && youtubePlayer.current.loadVideoById) {
      // 자동재생 prop에 따라 loadVideoById 또는 cueVideoById 사용
      if (autoplay === 1) {
        // mute 상태 먼저 설정
        if (mute === 1) {
          youtubePlayer.current.mute();
        }
        // 자동재생으로 영상 로드
        youtubePlayer.current.loadVideoById(videoId);
        console.log('useEffect에서 loadVideoById로 자동재생 시도됨');
      } else {
        // 자동재생 없이 영상 준비만
        youtubePlayer.current.cueVideoById(videoId);
      }
    }
    
    // 새로운 영상 세트를 위한 상태 완전 초기화
    setIsVideoReady(false);
    isVideoReadyRef.current = false;
    countdownRef.current = {
      currentCount: 5,
      timerId: null,
      isPaused: false
    };

    // 플레이어 준비 완료 시 호출
    const onPlayerReady = (event) => {
      // 플레이어가 준비되면 새 영상 로드 (기존 플레이어 재사용 시)
      if (youtubePlayer.current && youtubePlayer.current.loadVideoById) {
        // autoplay prop에 따라 적절한 메서드 사용
        if (autoplay === 1) {
          // mute 상태 먼저 설정
          if (mute === 1) {
            event.target.mute();
          }
          // 자동재생으로 영상 로드
          youtubePlayer.current.loadVideoById(videoId);
        } else {
          // 자동재생 없이 영상 준비만
          youtubePlayer.current.cueVideoById(videoId);
        }
      }
      
      // 자동재생이 활성화된 경우 추가 재생 시도
      if (autoplay === 1) {
        setTimeout(() => {
          try {
            // mute 상태 재확인
            if (mute === 1) {
              event.target.mute();
            }
            // 재생 시도
            event.target.playVideo();
          } catch (error) {
            console.warn('자동재생 실패:', error);
          }
        }, 100); // 잠시 후 재생 시도
      }
    };

         // 5초 카운트다운 시작 함수
     const startCountdown = () => {
        // 이미 완료되었거나 이미 실행 중이면 무시 (ref로 즉시 체크)
        if (isVideoReadyRef.current || countdownRef.current.timerId) {
          return;
        }
       
       const countInterval = setInterval(() => {
         // 독립적인 카운트다운 진행
         countdownRef.current.currentCount--;
         
        if (countdownRef.current.currentCount < 0) {
          // 명시적으로 카운트 초기화 (clearInterval 대신)
          countdownRef.current.currentCount = 0;
          countdownRef.current.timerId = null;
          setIsVideoReady(true);
          isVideoReadyRef.current = true;
          //console.log(`[YoutubeIframe] ✅ 카운트다운 완료 - videoId: ${videoId}`);
          
          // 즉시 onVideoReady 호출 (React.memo로 리렌더링 차단)
          if (onVideoReadyRef.current) {
            onVideoReadyRef.current(shortId);
          }
          
          // 완료 후 타이머 완전 정리
          countdownRef.current = {
            currentCount: 0,
            timerId: null,
            isPaused: false
          };
          
          // 타이머 명시적 정리
          clearInterval(countInterval);
          return; // 타이머 종료
        }
       }, 1000);
       
       countdownRef.current.timerId = countInterval;
     };

    // 플레이어 상태 변경 감지
    const onPlayerStateChange = (event) => {   
      
      // 이미 완료된 영상이면 모든 처리 중단 (ref로 즉시 체크)
      if (isVideoReadyRef.current) {
        return;
      }

      if (event.data === window.YT.PlayerState.PLAYING) {
        // 일시정지 후 재개인지 확인
        if (countdownRef.current.isPaused) {
          countdownRef.current.isPaused = false;
          startCountdown();
        } else {
          // 처음 재생 시작
          startCountdown();
        }
      }
        
      if (event.data === window.YT.PlayerState.PAUSED) {
        // 독립적인 카운트다운 중단 (명시적 초기화)
        if (countdownRef.current.timerId) {
          countdownRef.current.isPaused = true;
          clearInterval(countdownRef.current.timerId);
          countdownRef.current.timerId = null;
        }
      }
       
      if (event.data === window.YT.PlayerState.ENDED) {
        setIsVideoReady(true);
        isVideoReadyRef.current = true;
        // 완료 후 타이머 완전 정리
        countdownRef.current = {
          currentCount: 0,
          timerId: null,
          isPaused: false
        };
        
        // 즉시 onVideoReady 호출 (React.memo로 리렌더링 차단)
        if (onVideoReadyRef.current) {
          onVideoReadyRef.current(shortId);
        }
        
        if (onVideoEndRef.current) {
          onVideoEndRef.current(shortId);
        }
      }
    };

    // YouTube Player 설정 객체 생성
    const generatePlayerConfig = () => {
      const config = {
        videoId: videoId,
        playerVars: {
          autoplay: autoplay, // 자동재생 (숫자로 전달)
          mute: mute, // 소리 끄기 (숫자로 전달)
          controls: (loop === 1) ? 0 : 1, // 컨트롤 표시
          disablekb: 0, // 키보드 컨트롤 허용
          fs: 0,
          iv_load_policy: 3,
          modestbranding: 1,
          rel: 0,
          showinfo: 0,
          playsinline: 1,
          enablejsapi: 1, // YouTube API 활성화
          origin: window.location.origin,
          cc_load_policy: 0,
          color: 'white',
          hl: 'ko',
          loop: loop, // 루프 활성화
          volume: mute ? 0 : 100 // mute가 1이면 볼륨 0
        },
        events: {
          'onStateChange': onPlayerStateChange,
          'onReady': onPlayerReady
        }
      };

      // 루프가 활성화된 경우에만 playlist 파라미터 추가
      if (loop === 1) {
        config.playerVars.playlist = videoId;
      }

      return config;
    };

    // YouTube iframe API 로드
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // YouTube API 준비되면 플레이어 초기화
    const onYouTubeIframeAPIReady = () => {
      if (window.YT) {
        // 기존 플레이어가 있으면 새 영상 로드, 없으면 새로 생성
        if (youtubePlayer.current && youtubePlayer.current.loadVideoById) {
          // 자동재생 prop에 따라 loadVideoById 또는 cueVideoById 사용
          if (autoplay === 1) {
            // mute 상태 먼저 설정
            if (mute === 1) {
              youtubePlayer.current.mute();
            }
            // 자동재생으로 영상 로드
            youtubePlayer.current.loadVideoById(videoId);
            console.log('loadVideoById로 자동재생 시도됨');
          } else {
            // 자동재생 없이 영상 준비만
            youtubePlayer.current.cueVideoById(videoId);
          }
        } else {
          youtubePlayer.current = new window.YT.Player(iframeId, generatePlayerConfig());
        }
      }
    };

    // YouTube API가 이미 로드되어 있으면 바로 초기화
    if (window.YT && window.YT.Player) {
      onYouTubeIframeAPIReady();
    } else {
      // YouTube API 로드 대기 - 큐에 추가
      window._youtubeInitQueue.push(onYouTubeIframeAPIReady);
    }

    return () => {  
          // YouTube Player 정리 (언마운트 시에만 destroy)
          if (youtubePlayer.current) {
            youtubePlayer.current.destroy();
            youtubePlayer.current = null;
          }
          
          // 타이머 정리 (명시적 초기화)
          if (countdownRef.current.timerId) {
            countdownRef.current.timerId = null;
          }
        };
  }, [videoId, shortId]); // onVideoEnd, onVideoReady 제거

     return (
     <VideoContainer>
       <div id={iframeId} style={{ width: '100%', height: '100%', borderRadius: '15px' }} />
     </VideoContainer>
   )
 });

export default YoutubeIframe; 